import { useEffect, useMemo, useContext } from "react";
import { useOutletContext, useParams, useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, Stack, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useSettings } from "../../context/SettingsContext";
import { LayoutContext } from "../../context/LayoutContext";

import LeadHeader from "./LeadHeader";
import LeadDetailsSkeleton from "./LeadDetailsSkeleton";
import { LEAD_PAGE_BLOCKS } from "./Lead.config";
import { getStatusUI } from "../../config/settings.config";
import { getPipelineStage } from "../../config/pipeline.config";
import { useLocation } from "react-router-dom";

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useSettings();
  const { collapsed } = useContext(LayoutContext);
  const location = useLocation();
  const isPipelinePage = location.pathname.startsWith("/pipelines");

  const {
    activeLead: lead,
    loadLeadById,
    updateLeadField,
    clearActiveLead,
    addPayment,
    deletePayment,
    uploadAttachments,
    deleteAttachment,
    addBooking,
    updateBooking,
    deleteBooking,
  } = useOutletContext();

  const {
    dynamicStatus = [],
    pipelineKeys = [],
    maxSla = null,
  } = settings || {};

  useEffect(() => {
    clearActiveLead();
    loadLeadById(id);
  }, [id, loadLeadById]);

  const gradeStatusUIMap = useMemo(() => {
    const map = {};
    (settings?.grade_status || []).forEach((k) => (map[k] = getStatusUI(k)));
    return map;
  }, [settings]);

  const pipelineStatusUIMap = useMemo(() => {
    const map = {};
    (settings?.pipeline_status || []).forEach(
      (k) => (map[k] = getPipelineStage(k)),
    );
    return map;
  }, [settings]);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isLg = useMediaQuery(theme.breakpoints.between("lg", "xl"));
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));

  const responsiveColumns = useMemo(() => {
    if (isTablet) return 1;
    if (isLaptop) return collapsed ? 2 : 1;
    if (isLg) return collapsed ? 3 : 2;
    if (isXl) return 3;
    return 1;
  }, [isTablet, isLaptop, isLg, isXl, collapsed]);

  const features = useMemo(() => {
    if (!settings) return [];
    return isPipelinePage
      ? settings.pipeline_features || []
      : settings.lead_features?.features || [];
  }, [settings, isPipelinePage]);

  const blocks = useMemo(() => {
    const currentStatusKey = lead?.current_status?.key || "";

    // Define pre-consult statuses (show Consultancy bookings)
    const preConsultStatuses = [
      "qualified",
      "need_analysis",
      "booked",
      "dropped"
    ];

    // Define post-consult statuses (show Treatment bookings)
    const postConsultStatuses = [
      "post_consult",
      "proposal_shared",
      "negotiation",
      "treatment_booked",
      "won",
      "dropped"
    ];

    const isPreConsult = preConsultStatuses.includes(currentStatusKey);
    const isPostConsult = postConsultStatuses.includes(currentStatusKey);

    const normalized = features
      .filter((f) => {
        if (!f.enabled) return false;

        // For bookings, filter based on current status
        if (f.key.startsWith("bookings")) {
          const bookingType = f.meta?.type?.toLowerCase() || "";

          // Show consultancy (test2) bookings only in pre-consult stages
          if (bookingType === "test2" || bookingType === "consultancy") {
            return isPreConsult;
          }
          // Show treatment bookings only in post-consult stages
          if (bookingType === "treatment") {
            return isPostConsult;
          }
          // For other booking types, show them always
          return true;
        }

        return Boolean(LEAD_PAGE_BLOCKS[f.key]);
      })
      .sort((a, b) => a.order - b.order)
      .map((f) => {
        const baseKey = f.key.startsWith("bookings") ? "bookings" : f.key;

        return {
          ...LEAD_PAGE_BLOCKS[baseKey],
          position: f.position || "left",
          meta: f.meta || {},
          key: `${baseKey}_${f.meta?.type || "default"}`,
        };
      });

    let left = normalized.filter((b) => b.position === "left");
    let middle = normalized.filter((b) => b.position === "middle");
    let right = normalized.filter((b) => b.position === "right");

    if (responsiveColumns === 1) {
      return {
        left: [...left, ...middle, ...right],
        middle: [],
        right: [],
      };
    }

    if (responsiveColumns === 2) {
      return {
        left,
        middle: [...middle, ...right],
        right: [],
      };
    }

    return { left, middle, right };
  }, [features, responsiveColumns, lead?.current_status?.key]);

  const visibleColumns = useMemo(() => {
    return [blocks.left, blocks.middle, blocks.right].filter(
      (col) => col.length,
    );
  }, [blocks]);
  const bookingConfigs = useMemo(() => {
    const map = {};

    (settings?.pipeline_features || [])
      .filter((f) => f.key?.startsWith("bookings"))
      .forEach((f) => {
        if (f.meta?.type) {
          map[f.meta.type] = {
            label: f.label,
            key: f.key,
            enabled: f.enabled,
            order: f.order,
            position: f.position,
            meta: f.meta,
          };
        }
      });

    return map;
  }, [settings]);

  const finalColumns = Math.min(responsiveColumns, visibleColumns.length);

  const blockProps = useMemo(
    () => ({
      lead,
      leadId: id,
      maxSla,
      orderCost: lead?.custom_fields?.order_cost || 0,
      payments: lead?.payments || [],
      logs: lead?.call_logs || [],
      attachments: lead?.attachments || [],
      timeline: lead?.timeline || [],
      currentStatus: lead?.current_status,
      onUpdateFollowup: (key, val) =>
        updateLeadField(id, key, val, pipelineKeys),
      gradeStatusUIMap,
      pipelineStatusUIMap,
      updateLead: (key, val) => updateLeadField(id, key, val, pipelineKeys),
      onAddPayment: (p) => addPayment(id, p),
      onDeletePayment: (pid) => deletePayment(id, pid),
      onUpload: (f) => uploadAttachments(id, f),
      onDelete: (aid) => deleteAttachment(id, aid),
      statusList: dynamicStatus,
      followup: lead?.next_followup || null,
      followupStatus: lead?.followup_status || "pending",
      onSaveOrderCost: (val) =>
        updateLeadField(id, "custom_fields", {
          ...(lead?.custom_fields || {}),
          order_cost: val,
        }),
      addBooking,
      updateBooking,
      deleteBooking,
      bookingConfigs,
      refreshLead: () => loadLeadById(id),
    }),
    [
      lead,
      maxSla,
      updateLeadField,
      addPayment,
      deletePayment,
      uploadAttachments,
      deleteAttachment,
      dynamicStatus,
      gradeStatusUIMap,
      pipelineStatusUIMap,
      id,
      pipelineKeys,
      addBooking,
      updateBooking,
      deleteBooking,
      bookingConfigs,
    ],
  );

  if (!lead || lead._id !== id) {
    return <LeadDetailsSkeleton />;
  }

  if (!lead || !dynamicStatus.length) {
    return <LeadDetailsSkeleton />;
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, background: "#F8FAFC", maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" }} minHeight="90vh">
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: 2,
            color: "#64748B",
            "&:hover": {
              bgcolor: "#F1F5F9",
              color: "#7C3AED",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            fontSize: { xs: 20, sm: 24 },
            fontWeight: 700,
            color: "#1E293B",
          }}
        >
          Lead Profile
        </Typography>
      </Stack>

      <LeadHeader
        lead={lead}
        updateLead={(k, v) => updateLeadField(id, k, v, pipelineKeys)}
        dynamicStatus={dynamicStatus}
        leadSource={settings?.lead_features?.sources}
      />

      <Box
        sx={{
          mt: { xs: 2, sm: 3 },
          display: "grid",
          gridTemplateColumns:
            finalColumns === 1
              ? "1fr"
              : finalColumns === 2
                ? "1fr 1fr"
                : "1fr 1fr 1fr",
          gap: { xs: 2, sm: 3 },
          maxWidth: "100%",
        }}
      >
        {visibleColumns.map((column, i) => (
          <Box key={i} sx={{ minWidth: 0 }}>
            {column.map((block) => (
              <Box key={block.key} sx={{ mb: { xs: 2, sm: 2.5 } }}>
                {block.render({ ...blockProps, meta: block.meta })}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LeadDetails;
